import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button, Row, Col, Modal, Form, Table } from 'react-bootstrap';
import { round, uniq } from 'lodash';
import { PolicyBenefitLimit } from '@models/PolicyBenefitLimit';
import { selectHealthClaim } from '../selector';
import { billingItemsFetch, healthClaimBillingItemsBulkCreate } from '../action';
import { BillingItemEntry } from './billing-item-schema';

type Props = {
  show: boolean;
  onHide: () => void;
  healthClaimId: number;
  policyBenefitLimits: PolicyBenefitLimit[];
  businessPartnerId?: number;
};

const calculateSubtotal = (price: number, quantity: number, discount: number): number => {
  return round((price * quantity) * (1 - discount / 100), 2);
};

const BillingItemFormModal = ({ show, onHide, healthClaimId, policyBenefitLimits, businessPartnerId }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { billingItems, healthClaimBillingItems } = useSelector(selectHealthClaim);

  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedPolicyBenefitLimitId, setSelectedPolicyBenefitLimitId] = useState<string>('');
  const [selectedItems, setSelectedItems] = useState<BillingItemEntry[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  const categories = useMemo(() => {
    return uniq(policyBenefitLimits.map((pbl) => pbl.benefit));
  }, [policyBenefitLimits]);

  const filteredPolicyBenefitLimits = useMemo(() => {
    if (!selectedCategory) return [];
    return policyBenefitLimits.filter((pbl) => pbl.benefit === selectedCategory);
  }, [policyBenefitLimits, selectedCategory]);

  const selectedPolicyBenefitLimit = useMemo(() => {
    if (!selectedPolicyBenefitLimitId) return null;
    return policyBenefitLimits.find((pbl) => pbl.id === Number(selectedPolicyBenefitLimitId)) || null;
  }, [policyBenefitLimits, selectedPolicyBenefitLimitId]);

  // Filter out items already added to the claim
  const availableBillingItems = useMemo(() => {
    if (!billingItems) return null;
    const existingIds = new Set((healthClaimBillingItems ?? []).map((item) => item.billingItemId));
    return billingItems.filter((bi) => !existingIds.has(bi.id));
  }, [billingItems, healthClaimBillingItems]);

  // Filter by search query
  const filteredAvailableItems = useMemo(() => {
    if (!availableBillingItems) return null;
    if (!searchQuery.trim()) return availableBillingItems;
    const query = searchQuery.toLowerCase();
    return availableBillingItems.filter((bi) => bi.name.toLowerCase().includes(query));
  }, [availableBillingItems, searchQuery]);

  // Fetch billing items when policy benefit limit is selected
  useEffect(() => {
    if (!selectedPolicyBenefitLimit) return;

    dispatch(billingItemsFetch({
      insuranceCoverageId: selectedPolicyBenefitLimit.insuranceCoverageId,
      businessPartnerId,
    }));
  }, [selectedPolicyBenefitLimit, businessPartnerId]);

  const handleItemToggle = (billingItemId: number, name: string, priceAmount: string | null, checked: boolean) => {
    if (checked && selectedPolicyBenefitLimit) {
      const entry: BillingItemEntry = {
        billingItemId,
        policyBenefitLimitId: selectedPolicyBenefitLimit.id,
        itemName: name,
        subBenefit: selectedPolicyBenefitLimit.subBenefit,
        priceAmount: priceAmount ? Number(priceAmount) : 0,
        quantity: 1,
        discountPercentage: 0,
      };
      setSelectedItems((prev) => [...prev, entry]);
    } else {
      setSelectedItems((prev) => prev.filter((item) => item.billingItemId !== billingItemId));
    }
  };

  const handleItemFieldChange = (billingItemId: number, field: string, value: number) => {
    setSelectedItems((prev) =>
      prev.map((item) =>
        item.billingItemId === billingItemId ? { ...item, [field]: value } : item
      )
    );
  };

  const handleSubmit = () => {
    const validItems = selectedItems.filter(
      (item) => item.priceAmount > 0 && item.quantity >= 1 && item.discountPercentage >= 0 && item.discountPercentage <= 100
    );
    if (validItems.length === 0) return;

    dispatch(
      healthClaimBillingItemsBulkCreate({
        healthClaimId,
        items: validItems.map((item) => ({
          billingItemId: item.billingItemId,
          policyBenefitLimitId: item.policyBenefitLimitId,
          itemName: item.itemName,
          subBenefit: item.subBenefit,
          priceAmount: `${item.priceAmount}`,
          quantity: item.quantity,
          discountPercentage: `${item.discountPercentage}`,
        })),
      })
    );
    handleClose();
  };

  const handleClose = () => {
    setSelectedCategory('');
    setSelectedPolicyBenefitLimitId('');
    setSelectedItems([]);
    setSearchQuery('');
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>{t('billingDetails.addItems')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted">{t('billingDetails.addItemsDescription')}</p>

        <Row className="mb-3">
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>{t('billingDetails.selectCategory')}*</Form.Label>
              <Form.Select
                value={selectedCategory}
                onChange={(e) => {
                  setSelectedCategory(e.target.value);
                  setSelectedPolicyBenefitLimitId('');
                  setSelectedItems([]);
                }}
              >
                <option value="">{t('billingDetails.selectCategoryPlaceholder')}</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group>
              <Form.Label>{t('billingDetails.selectSubBenefit')}*</Form.Label>
              <Form.Select
                value={selectedPolicyBenefitLimitId}
                disabled={!selectedCategory}
                onChange={(e) => {
                  setSelectedPolicyBenefitLimitId(e.target.value);
                  setSelectedItems([]);
                  setSearchQuery('');
                }}
              >
                <option value="">{selectedCategory ? t('billingDetails.selectSubBenefitPlaceholder') : t('billingDetails.selectCategoryFirst')}</option>
                {filteredPolicyBenefitLimits.map((pbl) => (
                  <option key={pbl.id} value={pbl.id}>{pbl.subBenefit}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        {selectedPolicyBenefitLimit && availableBillingItems && availableBillingItems.length > 0 && (
          <>
            <Form.Control
              type="text"
              placeholder={t('billingDetails.searchItems')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="mb-3"
            />
            <Table striped bordered hover size="sm">
              <thead>
                <tr>
                  <th style={{ width: '40px' }}></th>
                  <th>{t('billingDetails.itemName')}</th>
                  <th style={{ width: '150px' }}>{t('billingDetails.price')}</th>
                  <th style={{ width: '100px' }}>{t('billingDetails.quantity')}</th>
                  <th style={{ width: '120px' }}>{t('billingDetails.discount')}</th>
                  <th style={{ width: '150px' }}>{t('billingDetails.subtotal')}</th>
                </tr>
              </thead>
              <tbody>
                {(filteredAvailableItems ?? []).map((billingItem) => {
                  const selected = selectedItems.find((si) => si.billingItemId === billingItem.id);
                  const isChecked = !!selected;
                  const subtotal = selected
                    ? calculateSubtotal(selected.priceAmount, selected.quantity, selected.discountPercentage)
                    : 0;

                  return (
                    <tr key={billingItem.id}>
                      <td>
                        <Form.Check
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => handleItemToggle(billingItem.id, billingItem.name, billingItem.partnerPriceAmount, e.target.checked)}
                        />
                      </td>
                      <td>{billingItem.name}</td>
                      <td>
                        {isChecked ? (
                          <Form.Control
                            type="number"
                            size="sm"
                            value={selected.priceAmount}
                            min={0.01}
                            step={0.01}
                            onChange={(e) => handleItemFieldChange(billingItem.id, 'priceAmount', Number(e.target.value))}
                          />
                        ) : (
                          billingItem.partnerPriceAmount ? Number(billingItem.partnerPriceAmount).toFixed(2) : '-'
                        )}
                      </td>
                      <td>
                        {isChecked ? (
                          <Form.Control
                            type="number"
                            size="sm"
                            value={selected.quantity}
                            min={1}
                            onChange={(e) => handleItemFieldChange(billingItem.id, 'quantity', Number(e.target.value))}
                          />
                        ) : '-'}
                      </td>
                      <td>
                        {isChecked ? (
                          <Form.Control
                            type="number"
                            size="sm"
                            value={selected.discountPercentage}
                            min={0}
                            max={100}
                            onChange={(e) => handleItemFieldChange(billingItem.id, 'discountPercentage', Number(e.target.value))}
                          />
                        ) : '-'}
                      </td>
                      <td>{isChecked ? subtotal.toFixed(2) : '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </>
        )}

        {selectedPolicyBenefitLimit && availableBillingItems && availableBillingItems.length === 0 && (
          <p className="text-muted">{t('billingDetails.noItemsAvailable')}</p>
        )}

        {selectedPolicyBenefitLimit && !availableBillingItems && (
          <p className="text-muted">Loading...</p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          {t('confirmModal.cancel')}
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={selectedItems.length === 0}
        >
          {`${t('billingDetails.addSelectedItems')} (${selectedItems.length})`}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export { BillingItemFormModal };
